import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, PlayCircle, RefreshCw, Search, ShieldAlert, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { RoutineTierCell, tierLabel } from '@/components/routines/tier';
import {
  BORDMITTEL_LABEL,
  RoutineCollectionsCell,
  normalizeCollections,
} from '@/components/routines/collections';
import { ResyncErgebnisDialog } from '@/components/routines/ResyncErgebnisDialog';
import { api } from '@/lib/api';
import {
  compareRoutineOrder,
  executionOrderOf,
  useResyncRoutines,
  useRoutines,
  type Routine,
  type RoutineResyncResponse,
} from '@/hooks/useRoutines';

/**
 * Service-Type-Lookup für die Routinen-Library.
 *
 * `routine.applicable_service_types` liefert reine UUIDs. Um daraus lesbare
 * Namen zu machen, laden wir die Service-Type-Liste (GET /service-types) und
 * bilden id -> display_name ab. Der Contract ist { data: [{ id, code,
 * display_name, ... }], total }; ältere Varianten (blankes Array) werden
 * tolerant behandelt. Ist kein Match vorhanden, fällt die UI auf den
 * UUID-Prefix zurück.
 */
interface ServiceTypeLite {
  id: string;
  code?: string | null;
  display_name?: string | null;
}

interface ServiceTypeListResponse {
  data: ServiceTypeLite[];
  total: number;
}

const EMPTY_SERVICE_TYPE_NAMES: ReadonlyMap<string, string> = new Map();

function useServiceTypeNames(): ReadonlyMap<string, string> {
  const query = useQuery<ReadonlyMap<string, string>>({
    queryKey: ['service-types', 'lookup'],
    queryFn: async () => {
      const resp = await api.get<ServiceTypeListResponse | ServiceTypeLite[]>('/service-types');
      const list = Array.isArray(resp) ? resp : (resp?.data ?? []);
      const map = new Map<string, string>();
      for (const st of list) {
        if (st.id) map.set(st.id, st.display_name ?? st.code ?? st.id);
      }
      return map;
    },
    staleTime: 15 * 60 * 1000,
  });
  return query.data ?? EMPTY_SERVICE_TYPE_NAMES;
}

/**
 * /routines - Routinen-Library (Sprint 5, WT-503).
 *
 * Tabellen-Listing aller im Backend registrierten Wartungsroutinen mit Suche.
 * Click auf eine Zeile -> Detail-View mit Playbook-Source.
 *
 * Sortiert wird nach execution_order (kleiner = läuft früher), damit die
 * Liste die Abarbeitungsfolge der Komplettwartung zeigt und nicht nur ein
 * Alphabet - genau daran ist "Update (apply)" vor "Update-Check" gerutscht.
 */

export const Route = createFileRoute('/routines/')({
  component: RoutinesPage,
});

function RoutinesPage(): JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const query = useRoutines();
  const resync = useResyncRoutines();
  const [resyncErgebnis, setResyncErgebnis] = useState<RoutineResyncResponse | null>(null);
  const serviceTypeNames = useServiceTypeNames();
  const routines: Routine[] = useMemo(
    () => [...(query.data?.data ?? [])].sort(compareRoutineOrder),
    [query.data]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return routines;
    return routines.filter((r: Routine) =>
      [r.code, r.display_name, r.description ?? '']
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [routines, search]);

  const columns = useMemo(
    (): ReadonlyArray<Column<Routine>> => [
      {
        id: 'reihenfolge',
        header: 'Reihenfolge',
        filter: false,
        headerClassName: 'w-24',
        className: 'font-mono tabular-nums',
        cell: (r) => (
          <span title="Reihenfolge der Abarbeitung - kleiner läuft früher">
            <span data-testid={`routine-order-${r.code}`}>{executionOrderOf(r)}</span>
          </span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        filterValue: (r) => [r.display_name, r.code, r.description ?? ''].join(' '),
        cell: (r) => (
          <div className="flex flex-col">
            <span className="font-medium">{r.display_name}</span>
            <span className="font-mono text-xs text-muted">{r.code}</span>
            {r.description && (
              <span className="mt-0.5 line-clamp-2 max-w-md text-xs text-muted">
                {r.description}
              </span>
            )}
          </div>
        ),
      },
      // "Service-Typ(en)" stimmte nicht mehr: in dieser Spalte
      // stehen seit dem 10.09.2026 auch OS-Familien und die
      // Warnung "keine Bindung". Wer nach dem hinterlegten
      // Service-Typ suchte, hielt ein blaues "OS: debian" für
      // einen falsch gepflegten Eintrag.
      {
        id: 'bindung',
        header: (
          <span title="Service-Typen und OS-Familien wirken zusammen (oder-verknüpft). Ohne Eintrag erreicht die Routine kein System.">
            Bindung
          </span>
        ),
        filterLabel: 'Bindung',
        filterValue: (r) => bindungFilterValue(r, serviceTypeNames),
        cell: (r) => <BindungCell routine={r} serviceTypeNames={serviceTypeNames} />,
      },
      {
        id: 'herkunft',
        header: 'Herkunft',
        filter: 'select',
        filterValue: (r) => {
          const namen = normalizeCollections(r.required_collections);
          return namen.length === 0 ? BORDMITTEL_LABEL : namen.join(', ');
        },
        cell: (r) => (
          <RoutineCollectionsCell
            collections={r.required_collections}
            data-testid={`routine-collections-${r.code}`}
          />
        ),
      },
      {
        id: 'reifegrad',
        header: 'Reifegrad',
        filter: 'select',
        filterValue: (r) => tierLabel(r.tier),
        cell: (r) => (
          <RoutineTierCell tier={r.tier} data-testid={`routine-tier-${r.code}`} />
        ),
      },
      {
        id: 'destruktiv',
        header: 'Destruktiv?',
        filter: 'select',
        filterValue: (r) => (r.is_destructive ? 'ja' : 'nein'),
        cell: (r) =>
          r.is_destructive ? (
            <Badge tone="danger">destruktiv</Badge>
          ) : (
            <Badge tone="neutral">read-only</Badge>
          ),
      },
      {
        id: 'snapshot-pflicht',
        header: 'Snapshot Pflicht?',
        filter: 'select',
        filterValue: (r) => (r.requires_snapshot ? 'ja' : 'nein'),
        cell: (r) =>
          r.requires_snapshot ? (
            <Badge tone="primary">
              <span className="inline-flex items-center gap-1">
                <ShieldAlert className="size-3" aria-hidden="true" /> Pflicht
              </span>
            </Badge>
          ) : (
            <span className="text-xs text-muted">optional</span>
          ),
      },
      {
        id: 'aktion',
        header: 'Aktion',
        filter: false,
        headerClassName: 'text-right',
        className: 'text-right',
        cell: (r) => (
          <Link to="/runs/new" search={{ routine: r.code }}>
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<PlayCircle className="size-3" />}
              onClick={(e) => e.stopPropagation()}
            >
              Starten
            </Button>
          </Link>
        ),
      },
    ],
    [serviceTypeNames],
  );

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <BookOpen className="size-5 text-primary" aria-hidden="true" />
            <h1 className="text-2xl font-semibold">Routinen-Library</h1>
          </div>
          <p className="text-muted">
            Wartungsroutinen (Ansible-Playbooks). Read-Only-View &mdash; aktiv ausführen via
            &laquo;Wartung starten&raquo;.
          </p>
          <p className="text-sm text-muted" data-testid="routines-order-hint">
            Sortiert nach Reihenfolge: kleinere Zahl läuft früher (z.&nbsp;B. Update-Check
            vor Update). Der Wert steht im Meta-Header des Playbooks
            (<code className="font-mono text-xs">execution_order</code>) und ist im Detail
            einer Routine änderbar.
          </p>
          {/* Ohne diesen Satz ist "beta" ein Wort ohne Inhalt - und genau daran
              ist das Feld vorher gescheitert: es stand überall dasselbe. */}
          <p className="text-sm text-muted" data-testid="routines-tier-hint">
            <strong>Reifegrad</strong> sagt, wie viel Erprobung belegt ist, nicht wie gut
            die Routine gemeint ist: <em>produktiv</em> = mindestens ein erfolgreicher
            Durchlauf auf einem echten System belegt, <em>beta</em> = laeuft an, aber kein
            sauberer Durchlauf belegt, <em>experimentell</em> = noch kein einziger Lauf
            belegt. Gepflegt im Meta-Header des Playbooks
            (<code className="font-mono text-xs">tier</code>).
          </p>
          {/* Bis 26.08.2026 stand nirgends im Tool, dass die Windows-Routinen
              eine Galaxy-Collection brauchen - requirements.yml behauptete
              sogar das Gegenteil. Wer es nicht weiß, sucht den Fehler beim
              Kundensystem statt am eigenen Rechner. */}
          <p className="text-sm text-muted" data-testid="routines-collections-hint">
            <strong>Herkunft</strong> sagt, woher die Module einer Routine kommen:{' '}
            <em>nur Bordmittel</em> = laeuft mit <code className="font-mono text-xs">
              ansible-core
            </code>{' '}
            auf jedem Rechner. Steht dort eine Collection, muss sie auf dem
            Wartungs-PC installiert sein
            (<code className="font-mono text-xs">
              ansible-galaxy collection install
            </code>) &mdash; sonst scheitert der Lauf am ersten Task. Der Client
            prueft das vor dem Start; gepflegt wird es im Meta-Header
            (<code className="font-mono text-xs">required_collections</code>).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Bis zum 10.09.2026 kam eine geänderte Routine nur über einen
              Backend-Neustart ins Tool - und der sperrt den Secrets-Manager
              und erzwingt einen neuen Login. Für eine Zeile im Meta-Header. */}
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="size-4" />}
            loading={resync.isPending}
            onClick={() =>
              resync.mutate(undefined, {
                onSuccess: (ergebnis) => setResyncErgebnis(ergebnis),
              })
            }
            title="Playbooks aus dem Routinen-Repo neu einlesen (ohne Backend-Neustart)"
            data-testid="routines-resync"
          >
            Routinen neu einlesen
          </Button>
          <Link to="/ai/suggest">
            <Button variant="secondary" leftIcon={<Sparkles className="size-4" />}>
              Neue mit KI
            </Button>
          </Link>
          <Link to="/runs/new">
            <Button leftIcon={<PlayCircle className="size-4" />}>Wartung starten</Button>
          </Link>
        </div>
      </header>

      {resync.isError && <ErrorAlert error={resync.error} />}
      <ResyncErgebnisDialog ergebnis={resyncErgebnis} onClose={() => setResyncErgebnis(null)} />

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted"
              aria-hidden="true"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Suche (Code / Name / Beschreibung) ..."
              aria-label="Routinen durchsuchen"
              className="rounded-md border border-border bg-surface px-8 py-2 text-sm placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid="routines-search"
            />
          </div>
          <span className="text-xs text-muted">
            {filtered.length} / {routines.length} Routinen
          </span>
        </div>

        {query.isLoading && <SkeletonRows rows={4} cols={6} />}
        {query.isError && <ErrorAlert error={query.error} />}
        {!query.isLoading && !query.isError && filtered.length === 0 && (
          <EmptyState
            icon={<BookOpen className="size-8" />}
            title="Keine Routinen gefunden"
            description={
              search
                ? 'Keine Treffer für diese Suche.'
                : 'Backend liefert (noch) keine Routinen.'
            }
          />
        )}
        {filtered.length > 0 && (
          <DataTable
            caption="Routinen-Library"
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            storageKey="routinen"
            onRowClick={(r) =>
              void navigate({ to: '/routines/$slug', params: { slug: r.code } })
            }
            rowTestId={(r) => `routine-row-${r.code}`}
          />
        )}
      </Card>
    </div>
  );
}

function bindungFilterValue(
  routine: Routine,
  serviceTypeNames: ReadonlyMap<string, string>,
): string {
  const types = routine.applicable_service_types.map(
    (id) => serviceTypeNames.get(id) ?? id.slice(0, 8),
  );
  const families = routine.applicable_os_families ?? [];
  if (types.length === 0 && families.length === 0) {
    return 'keine Bindung erreicht kein System';
  }
  return [
    ...types,
    ...families.map((family) => `Betriebssystem: ${family}`),
  ].join(' ');
}

function BindungCell({
  routine,
  serviceTypeNames,
}: {
  routine: Routine;
  serviceTypeNames: ReadonlyMap<string, string>;
}): JSX.Element {
  return (
    <span className="flex flex-wrap gap-1">
      {/*
        Bis zum 10.09.2026 stand hier "alle", wenn die Typenliste leer war -
        und das stimmte damals sogar: eine Routine ohne Typen traf jedes
        System mit aktivem SSH-Profil, auch OPNsense und Windows. Seit der
        Bindung über os_families ist die leere Liste das Gegenteil, nämlich
        "passt auf nichts". Ein Label, das die Umkehrung nicht mitmacht,
        wäre die gefährlichste Sorte Anzeige.
      */}
      {routine.applicable_service_types.length === 0 &&
      (routine.applicable_os_families?.length ?? 0) === 0 ? (
        /* Die eigentliche Aussage stand nur im title-Attribut und kam
           damit auf dem Touchgerät gar nicht an. */
        <span
          className="flex flex-col gap-0.5"
          title="Weder applicable_service_types noch applicable_os_families im Meta-Header."
        >
          <Badge tone="warning">
            <span className="text-[10px]">keine Bindung</span>
          </Badge>
          <span className="text-[10px] text-warning">erreicht kein System</span>
        </span>
      ) : (
        <>
          {routine.applicable_service_types.map((id) => {
            const name = serviceTypeNames.get(id);
            return (
              <Badge key={id} tone="neutral">
                {name ? (
                  <span className="text-[10px]">{name}</span>
                ) : (
                  <span className="font-mono text-[10px]" title={id}>
                    {id.slice(0, 8)}
                  </span>
                )}
              </Badge>
            );
          })}
          {(routine.applicable_os_families ?? []).map((family) => (
            <span
              key={`os-${family}`}
              title="Betriebssystem-Familie (systems.os_family) — gilt zusätzlich zu den Service-Typen"
            >
              <Badge tone="primary">
                <span className="text-[10px]">Betriebssystem: {family}</span>
              </Badge>
            </span>
          ))}
        </>
      )}
    </span>
  );
}
